import React, { useState, useEffect, useMemo } from 'react';
import { useSearchParams, useNavigate } from 'react-router-dom';
import axios from 'axios';
import {
  Box,
  Container,
  Typography,
  Card,
  CardContent,
  CircularProgress,
  Alert,
  Tabs,
  Tab,
  Button,
  IconButton,
  Badge,
  ThemeProvider,
  createTheme,
  CssBaseline,
  Stack,
  Fab,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Divider,
  List,
  ListItem,
  ListItemText
} from '@mui/material';
import RestaurantMenuIcon from '@mui/icons-material/RestaurantMenu';
import ReceiptLongIcon from '@mui/icons-material/ReceiptLong';
import AddIcon from '@mui/icons-material/Add';
import RemoveIcon from '@mui/icons-material/Remove';
import ShoppingCartIcon from '@mui/icons-material/ShoppingCart';
import { useCurrency, formatCurrency } from '../../utils/currency';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';

const theme = createTheme({
  palette: {
    mode: 'light',
    primary: {
      main: '#2e7d32', // Premium food green
    },
    background: {
      default: '#f8fafc',
    },
  },
  typography: {
    fontFamily: '"Inter", "Roboto", "Helvetica", "Arial", sans-serif',
    h5: { fontWeight: 700 },
    h6: { fontWeight: 600 },
    subtitle1: { fontWeight: 600 }
  },
  shape: { borderRadius: 12 },
  components: {
    MuiCard: {
      styleOverrides: {
        root: {
          boxShadow: '0 4px 6px -1px rgb(0 0 0 / 0.1), 0 2px 4px -2px rgb(0 0 0 / 0.1)',
          border: '1px solid #e2e8f0',
          marginBottom: '16px'
        }
      }
    },
    MuiTab: {
      styleOverrides: {
        root: {
          fontWeight: 600,
          textTransform: 'none',
          fontSize: '1rem',
        }
      }
    }
  }
});

const GuestMenu = () => {
  const { currency } = useCurrency();
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const guestId = searchParams.get('guestId');
  const roomId = searchParams.get('roomId');
  const tenantId = searchParams.get('tenantId') || '1';

  const [categories, setCategories] = useState([]);
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  
  const [activeCategoryId, setActiveCategoryId] = useState('');
  const [cart, setCart] = useState({}); // { itemId: quantity }
  
  const [isCheckoutOpen, setIsCheckoutOpen] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [successOrder, setSuccessOrder] = useState(null);

  useEffect(() => {
    const fetchMenu = async () => {
      try {
        setLoading(true);
        const baseUrl = process.env.REACT_APP_API_BASE_URL_PMS || 'http://localhost:3000';
        
        // Fetch Categories
        const catRes = await axios.get(`${baseUrl}/api/v1/public/restaurant/categories`, {
          headers: { 'x-tenant-id': tenantId }
        });
        
        // Fetch Items
        const itemRes = await axios.get(`${baseUrl}/api/v1/public/restaurant/items`, {
          headers: { 'x-tenant-id': tenantId }
        });
        
        const fetchedCategories = catRes.data.data || [];
        setCategories(fetchedCategories);
        setItems(itemRes.data.data || []);
        
        if (fetchedCategories.length > 0) {
          setActiveCategoryId(fetchedCategories[0].id);
        }
      } catch (err) {
        setError(err.response?.data?.message || 'Unable to load menu right now.');
      } finally {
        setLoading(false);
      }
    };

    fetchMenu();
  }, [tenantId]);

  const handleCategoryChange = (event, newValue) => {
    setActiveCategoryId(newValue);
  };

  const handleAddToCart = (itemId, change) => {
    setCart(prev => {
      const current = prev[itemId] || 0;
      const next = Math.max(0, current + change);
      const newCart = { ...prev };
      if (next === 0) delete newCart[itemId];
      else newCart[itemId] = next;
      return newCart;
    });
  };

  const currentItems = useMemo(() => {
    return items.filter(item => item.categoryId === activeCategoryId && item.isAvailable !== false);
  }, [items, activeCategoryId]);

  const cartItemsList = useMemo(() => {
    return Object.entries(cart).map(([itemId, quantity]) => {
      const item = items.find(i => i.id === itemId);
      return { ...item, quantity };
    }).filter(i => i.id);
  }, [cart, items]);

  const cartTotalPrice = cartItemsList.reduce((sum, item) => sum + (parseFloat(item.price) * item.quantity), 0);
  const cartTotalItems = Object.values(cart).reduce((a, b) => a + b, 0);

  const handleCheckout = async () => {
    try {
      setIsSubmitting(true);
      const baseUrl = process.env.REACT_APP_API_BASE_URL_PMS || 'http://localhost:3000';
      
      const payload = {
        guestId,
        roomId,
        items: cartItemsList.map(item => ({
          menuItemId: item.id,
          quantity: item.quantity,
          unitPrice: parseFloat(item.price)
        }))
      };

      const response = await axios.post(`${baseUrl}/api/v1/public/restaurant/orders/guest`, payload, {
        headers: { 'x-tenant-id': tenantId }
      });
      
      // Clear cart, close dialog, and show success screen
      setCart({});
      setIsCheckoutOpen(false);
      setSuccessOrder(response.data.data);
      
    } catch (err) {
      alert(err.response?.data?.message || 'Failed to place order.');
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <ThemeProvider theme={theme}>
      <CssBaseline />
      <Box sx={{ minHeight: '100vh', pb: 12 }}>
        {/* Mobile Header */}
        <Box sx={{ bgcolor: 'primary.main', color: 'white', pt: 6, pb: 3, px: 3, mb: 0, boxShadow: '0 4px 6px -1px rgb(0 0 0 / 0.1)' }}>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            <Typography variant="h5" sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <RestaurantMenuIcon /> Menu
            </Typography>
            <IconButton color="inherit" onClick={() => navigate(`/orders?guestId=${guestId}&roomId=${roomId}&tenantId=${tenantId}`)}>
              <ReceiptLongIcon />
            </IconButton>
          </Box>
          <Typography variant="body1" sx={{ mt: 1, opacity: 0.9 }}>
            What would you like to order today?
          </Typography>
        </Box>

        {loading ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', mt: 8 }}>
            <CircularProgress />
          </Box>
        ) : error ? (
          <Container maxWidth="sm"><Alert severity="error" sx={{ mt: 2 }}>{error}</Alert></Container>
        ) : (
          <>
            {/* Category Tabs */}
            <Box sx={{ borderBottom: 1, borderColor: '#eaecf0', bgcolor: 'white', position: 'sticky', top: 0, zIndex: 10 }}>
              <Tabs 
                value={activeCategoryId} 
                onChange={handleCategoryChange} 
                variant="scrollable"
                scrollButtons="auto"
              >
                {categories.map(cat => (
                  <Tab key={cat.id} label={cat.name} value={cat.id} />
                ))}
              </Tabs>
            </Box>

            {/* Menu Items */}
            <Container maxWidth="sm" sx={{ mt: 3 }}>
              {currentItems.length === 0 ? (
                <Typography color="text.secondary" textAlign="center" mt={4}>
                  No items available in this category.
                </Typography>
              ) : (
                currentItems.map(item => (
                  <Card key={item.id} sx={{ display: 'flex', p: 2, alignItems: 'center', gap: 2 }}>
                    <Box sx={{ flex: 1 }}>
                      <Typography variant="subtitle1">{item.name}</Typography>
                      <Typography variant="body2" color="text.secondary" sx={{ mb: 1, minHeight: '40px' }}>
                        {item.description}
                      </Typography>
                      <Typography variant="h6" color="primary.main">
                        {formatCurrency(parseFloat(item.price || 0), currency)}
                      </Typography>
                    </Box>
                    <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 1 }}>
                      {cart[item.id] ? (
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, bgcolor: '#f1f5f9', borderRadius: 8, p: 0.5 }}>
                          <IconButton size="small" onClick={() => handleAddToCart(item.id, -1)}><RemoveIcon fontSize="small" /></IconButton>
                          <Typography fontWeight="bold" sx={{ minWidth: 20, textAlign: 'center' }}>{cart[item.id]}</Typography>
                          <IconButton size="small" color="primary" onClick={() => handleAddToCart(item.id, 1)}><AddIcon fontSize="small" /></IconButton>
                        </Box>
                      ) : (
                        <Button variant="outlined" color="primary" size="small" sx={{ borderRadius: 8, px: 3 }} onClick={() => handleAddToCart(item.id, 1)}>
                          Add
                        </Button>
                      )}
                    </Box>
                  </Card>
                ))
              )}
            </Container>

            {/* Floating Checkout Button */}
            {cartTotalItems > 0 && (
              <Fab 
                variant="extended" 
                color="primary" 
                onClick={() => setIsCheckoutOpen(true)}
                sx={{ position: 'fixed', bottom: 24, left: '50%', transform: 'translateX(-50%)', width: '90%', maxWidth: 'sm', py: 3, fontWeight: 'bold', fontSize: '1.1rem', justifyContent: 'space-between', px: 4 }}
              >
                <Badge badgeContent={cartTotalItems} color="error" sx={{ mr: 2 }}>
                  <ShoppingCartIcon />
                </Badge>
                View Cart ({formatCurrency(cartTotalPrice, currency)})
              </Fab>
            )}

            {/* Checkout Dialog */}
            <Dialog 
              open={isCheckoutOpen} 
              onClose={() => !isSubmitting && setIsCheckoutOpen(false)}
              fullWidth
              maxWidth="sm"
            >
              <DialogTitle sx={{ fontWeight: 'bold' }}>Review Your Order</DialogTitle>
              <DialogContent dividers>
                <List disablePadding>
                  {cartItemsList.map((item) => (
                    <ListItem key={item.id} disableGutters sx={{ py: 1 }}>
                      <ListItemText 
                        primary={<Typography fontWeight="bold">{item.quantity}x {item.name}</Typography>}
                      />
                      <Typography fontWeight="bold">{formatCurrency((item.quantity * item.price) || 0, currency)}</Typography>
                    </ListItem>
                  ))}
                  <Divider sx={{ my: 2 }} />
                  <ListItem disableGutters>
                    <ListItemText 
                      primary={<Typography variant="h6">Subtotal</Typography>}
                      secondary={<Typography variant="caption" color="text.secondary">* Taxes will be calculated and added to your room folio at checkout.</Typography>}
                    />
                    <Typography variant="h6" color="primary.main">{formatCurrency(cartTotalPrice, currency)}</Typography>
                  </ListItem>
                </List>
              </DialogContent>
              <DialogActions sx={{ p: 2, display: 'flex', gap: 2 }}>
                <Button 
                  onClick={() => setIsCheckoutOpen(false)} 
                  disabled={isSubmitting}
                  sx={{ flex: 1, borderRadius: 8, fontWeight: 'bold' }}
                >
                  Back to Menu
                </Button>
                <Button 
                  variant="contained" 
                  color="primary" 
                  onClick={handleCheckout} 
                  disabled={isSubmitting}
                  sx={{ flex: 1, borderRadius: 8, fontWeight: 'bold' }}
                >
                  {isSubmitting ? <CircularProgress size={24} color="inherit" /> : 'Place Order'}
                </Button>
              </DialogActions>
            </Dialog>

            {/* Success Order Dialog */}
            <Dialog 
              open={Boolean(successOrder)} 
              onClose={() => navigate(`/orders?guestId=${guestId}&roomId=${roomId}&tenantId=${tenantId}`)}
              fullWidth
              maxWidth="xs"
              PaperProps={{ sx: { borderRadius: 4, textAlign: 'center', p: 2 } }}
            >
              <DialogContent sx={{ pb: 1 }}>
                <CheckCircleIcon color="success" sx={{ fontSize: 80, mb: 2 }} />
                <Typography variant="h5" fontWeight="bold" gutterBottom>
                  Order Placed!
                </Typography>
                <Typography variant="body1" color="text.secondary" sx={{ mb: 3 }}>
                  Your order has been sent to the kitchen successfully.
                </Typography>
                
                <Box sx={{ bgcolor: '#f1f5f9', p: 2, borderRadius: 2, mb: 3 }}>
                  <Typography variant="body2" color="text.secondary" gutterBottom>
                    ORDER ID
                  </Typography>
                  <Typography variant="h6" fontWeight="bold" color="primary.main">
                    {successOrder?.orderId || 'PENDING'}
                  </Typography>
                </Box>
              </DialogContent>
              <DialogActions sx={{ p: 2, justifyContent: 'center' }}>
                <Button 
                  variant="contained" 
                  color="primary" 
                  size="large"
                  fullWidth
                  onClick={() => navigate(`/orders?guestId=${guestId}&roomId=${roomId}&tenantId=${tenantId}`)}
                  sx={{ borderRadius: 8, fontWeight: 'bold', py: 1.5 }}
                >
                  Track My Order
                </Button>
              </DialogActions>
            </Dialog>
          </>
        )}
      </Box>
    </ThemeProvider>
  );
};

export default GuestMenu;
